import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { DeskGlanceAgingBuckets } from "@/shared/api/desk";
import { HomeWidget } from "./HomeWidget";
import { formatGlanceMoney } from "./owner-glance-math";

const BUCKETS: { key: keyof DeskGlanceAgingBuckets; label: string }[] = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30" },
  { key: "d31_60", label: "31–60" },
  { key: "d61_90", label: "61–90" },
  { key: "d90_plus", label: "90+" },
];

/** To-collect aging bars — plan/50 wide widget. */
export function CollectAgingWidget(props: {
  companySlug: string;
  currencyCode: string;
  aging: DeskGlanceAgingBuckets | null | undefined;
  className?: string;
}) {
  const { companySlug, currencyCode, aging, className } = props;
  if (!aging) return null;

  const amounts = BUCKETS.map((b) => Number(aging[b.key]) || 0);
  const max = Math.max(...amounts, 1);
  const hasAny = amounts.some((n) => n > 0);
  if (!hasAny) return null;

  return (
    <HomeWidget
      title="To collect aging"
      description="Open sales orders by days past payment due"
      size="wide"
      className={cn(className)}
      actions={
        <Link
          to={`/c/${companySlug}/sales/to-collect`}
          className="shrink-0 text-xs font-medium text-[var(--color-primary)] no-underline hover:underline"
        >
          To collect
        </Link>
      }
    >
      <ul className="m-0 list-none space-y-2 p-0">
        {BUCKETS.map((b) => {
          const raw = aging[b.key];
          const n = Number(raw) || 0;
          const money =
            formatGlanceMoney(raw, currencyCode, {
              maxFractionDigits: 0,
              zeroOk: true,
            }) ?? raw;
          const pct = Math.round((n / max) * 100);
          return (
            <li key={b.key}>
              <Link
                to={`/c/${companySlug}/sales/to-collect`}
                className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-2 no-underline"
              >
                <span className="text-xs font-medium text-[var(--color-muted)]">
                  {b.label}
                </span>
                <span
                  className="h-2 overflow-hidden rounded-full bg-[var(--color-border)]/50"
                  aria-hidden
                >
                  <span
                    className="block h-full rounded-full bg-[var(--color-primary)]/80"
                    style={{ width: `${pct}%` }}
                  />
                </span>
                <span className="min-w-[4.5rem] text-right text-xs tabular-nums text-[var(--color-foreground)]">
                  {money}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </HomeWidget>
  );
}
